// Opt-in precise geolocation for the chat.
//
// IP geolocation (the default) is inherently coarse — city-level at best and
// often wrong for mobile/CGNAT/VPN clients. When the user opts in, we read the
// browser Geolocation API (GPS/Wi-Fi, ~10–50 m) and send the raw coordinates to
// the backend, which reverse-geocodes them to bairro/cidade (Google Geocoding,
// Redis-cached) and prefers them over the IP fallback. Reverse-geocoding lives
// server-side so the provider/key are centralized and the Redis cache absorbs
// load (users in the same ~110 m cell share one lookup).

export interface PreciseLocation {
  lat: number;
  lng: number;
}

// Current geolocation permission state, without triggering a prompt. Used to
// silently refresh the coords on app load when the user already granted it.
export async function geolocationPermissionState(): Promise<PermissionState | 'unsupported'> {
  try {
    if (typeof navigator === 'undefined' || !(navigator as any).permissions) return 'unsupported';
    const status = await (navigator as any).permissions.query({ name: 'geolocation' as PermissionName });
    return status.state as PermissionState;
  } catch {
    return 'unsupported';
  }
}

// Request the browser's precise coordinates (prompts for permission the first
// time). Resolves null on denial / unsupported / error — callers must fall back
// gracefully (the backend keeps the IP location).
export function requestPreciseLocation(opts?: { highAccuracy?: boolean }): Promise<PreciseLocation | null> {
  return new Promise((resolve) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      resolve(null);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => resolve(null),
      { enableHighAccuracy: opts?.highAccuracy ?? true, timeout: 10000, maximumAge: 300000 },
    );
  });
}
